// Turning spreadsheet text into typed values and back. Everything that reads
// or writes a cell goes through here, so the table, the form, import and
// export all agree on what "30M" or "23.5 EUR" means.

const CURRENCY_SYMBOLS = { "€": "EUR", $: "USD", "£": "GBP", "₺": "TRY" };

// Words a sheet may use for a currency, as written in a cell (any case).
// Only these count: a word that isn't one ("rev share") is not a currency.
const CURRENCY_WORDS = {
  EUR: "EUR",
  EURO: "EUR",
  EUROS: "EUR",
  USD: "USD",
  GBP: "GBP",
  TRY: "TRY",
  TL: "TRY",
  YTL: "TRY",
};

export const CURRENCIES = ["EUR", "USD", "GBP", "TRY"];

/* ---------- Lists: "food, home, garden" ---------- */

export function splitList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (value == null) return [];
  return String(value)
    .split(/[,;|\n/]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

export function joinList(list) {
  return (list || []).join(", ");
}

/* ---------- Reach: "30M", "1.2M", "800K" ---------- */

const REACH_UNITS = { K: 1e3, M: 1e6, B: 1e9 };

export function parseReach(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value == null) return null;
  const text = String(value).trim().toUpperCase().replace(/\s+/g, "");
  if (!text) return null;
  const match = text.match(/^([\d.,]+)([KMB])?$/);
  if (!match) return null;
  // "1,5M" (decimal comma) vs "1,500,000" (thousands separators)
  const digits = match[2] ? match[1].replace(",", ".") : match[1].replace(/,/g, "");
  const n = parseFloat(digits);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * (REACH_UNITS[match[2]] || 1));
}

export function formatReach(n) {
  if (n == null) return "";
  for (const [unit, size] of [["B", 1e9], ["M", 1e6], ["K", 1e3]]) {
    if (n >= size) {
      const v = n / size;
      return `${Number.isInteger(v) ? v : v.toFixed(1).replace(/\.0$/, "")}${unit}`;
    }
  }
  return String(n);
}

/* ---------- Money: "23.5 EUR", "€23.5" ---------- */

export function parseMoney(value, fallbackCurrency = "EUR") {
  if (typeof value === "number") return { amount: value, currency: fallbackCurrency };
  if (value == null || String(value).trim() === "") return { amount: null, currency: fallbackCurrency };
  const text = String(value).trim();

  let currency = fallbackCurrency;
  const word = (text.match(/[A-Za-z]+/g) || []).find((w) => CURRENCY_WORDS[w.toUpperCase()]);
  if (word) currency = CURRENCY_WORDS[word.toUpperCase()];
  for (const [symbol, iso] of Object.entries(CURRENCY_SYMBOLS)) {
    if (text.includes(symbol)) currency = iso;
  }

  const num = text.replace(/[^\d.,-]/g, "").replace(",", ".");
  const amount = parseFloat(num);
  return { amount: Number.isFinite(amount) ? amount : null, currency };
}

export function formatMoney(amount, currency) {
  if (amount == null) return "";
  return `${Number(amount).toFixed(2)} ${currency || ""}`.trim();
}

/* ---------- Contact: "Colin Ramsay (cramsay@axiommedia.co)" ---------- */

const EMAIL = /[^\s<>()]+@[^\s<>()]+\.[^\s<>()]+/;

export function parseContact(value) {
  if (value == null) return { name: "", email: "" };
  const text = String(value).trim();
  const email = (text.match(EMAIL) || [""])[0];
  const name = text
    .replace(email, "")
    .replace(/[()<>]/g, "")
    .trim();
  return { name, email };
}

export function formatContact(name, email) {
  if (name && email) return `${name} (${email})`;
  return name || email || "";
}

/* ---------- Misc ---------- */

export function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Case-insensitive de-duplication that keeps the first spelling seen.
export function uniqueCaseless(values) {
  const seen = new Map();
  values.forEach((v) => {
    const key = v.toLowerCase();
    if (!seen.has(key)) seen.set(key, v);
  });
  return [...seen.values()];
}
